/**
 * eventBridge extension — IEditorEventHandler 31개 콜백 중 NgwEditor 가 props 로 노출하지 않는
 * 표면을 PM extension 자체로 흡수한다 (D-N7, PR-N6).
 *
 * 흡수 대상 (사용자 결정 #1: 4 카테고리 모두 활용 / #7: copy/cut 만 PM plugin, PreProcess 영구 미지원):
 *   - `copy` / `cut` DOM 이벤트 → `handler.onCopyCut?({ clipboardData, type })`
 *     TinyMCE `editor.on('copy cut', ...)` 평행 (editorExt.ts L463~L500). user gesture 컨텍스트
 *     보존을 위해 동기 분기. view.setProps({ handleDOMEvents }) 로 등록.
 *   - selection change / format change 비교는 어댑터 (NgwEditorLifecycle) 가 onChange 시점에 직접
 *     수행. 본 extension 의 setup 콜백은 그 비교를 위해 view 를 어댑터에 전달한다 (디바운싱 타이머는
 *     lifecycle 멤버 — 사용자 결정 #2).
 *
 * **PreProcess 는 본 extension 에서 영구 미지원** (사용자 결정 #7): NgwEditor 의 `getHTML`
 * 외부 호출 패턴에서 PreProcess hook 의 본문 직렬화 직전 단계가 자연스럽지 않다. EmailForm 의
 * 본문 가져오는 시점에 직접 처리하는 흐름이 정확.
 *
 * **PR-N9a-2 (2026-05-18) 보강**: `NgwEditorContent.getDataPreprocessed()` 가 caller-trigger
 * 발화 경로로 onPreProcess 핸들러를 호출한다. 즉 PM extension 발화 (= 본 결정 #7 의 범위) 는
 * 여전히 영구 미지원이지만, 어댑터 표면의 명시적 메서드 발화는 도입됨. inline 이미지 cid 치환
 * 의 분리 분석은 [docs/iframe-editor/07-inline-image-onpreprocess.md] 와 openspec change
 * `editor-process-img/` 의 design.md §6.5 참조.
 */

import type { NgwExtension, EditorView } from '@ngweditor/react'
import type { EditorCopyCutEvent } from '../../types'
import type { NgwEditorAdapter } from '../NgwEditorAdapter'

export function createEventBridgeExtension(adapter: NgwEditorAdapter): NgwExtension {
  return {
    name: 'event-bridge',
    setup: ({ editor }) => {
      const { view } = editor
      adapter.lifecycle.attachEventBridgeView(view)

      view.setProps({
        handleDOMEvents: {
          copy: (_v: EditorView, event: Event): boolean => {
            forwardClipboardEvent(adapter, event, 'copy')
            return false
          },
          cut: (_v: EditorView, event: Event): boolean => {
            forwardClipboardEvent(adapter, event, 'cut')
            return false
          },
        },
      })
    },
  }
}

function forwardClipboardEvent(
  adapter: NgwEditorAdapter,
  event: Event,
  type: 'copy' | 'cut',
): void {
  const handler = adapter.getHandler()
  if (handler?.onCopyCut == null) {
    return
  }
  const clipboardEvent = event as ClipboardEvent
  const payload: EditorCopyCutEvent = {
    clipboardData: clipboardEvent.clipboardData ?? null,
    type,
  }
  handler.onCopyCut(payload)
}
